import { lazy } from 'react';

import { FullScreenLayout } from '@truvhq/ui';
import { Redirect, useLocation } from 'react-router-dom';

import { withChunkRetry } from 'utils/withChunkRetry';

import { useAuth } from 'hooks/useAuth';

import { AuthGuard } from 'components/AuthGuard';
import { GuestGuard } from 'components/GuestGuard';
import { PermissionGuard } from 'components/PermissionGuard';

import type { Permissions } from 'types/permissions';

import { TwoPaneLayout } from 'layouts';
import type { Routes } from 'RenderRoutes';
import { RenderRoutes } from 'RenderRoutes';
import { urls } from 'urls';

const UsersPage = lazy(() => withChunkRetry(() => import('views/pages/Users')));
const LoginView = lazy(() => withChunkRetry(() => import('views/auth/LoginView')));
const SignUpView = lazy(() => withChunkRetry(() => import('views/auth/SignUpView')));
const HomePage = lazy(() => withChunkRetry(() => import('views/pages/HomePage')));
const EmulatorPage = lazy(() => withChunkRetry(() => import('views/pages/EmulatorView')));
const WorkflowsPage = lazy(() => withChunkRetry(() => import('views/pages/Workflows')));
const TasksPage = lazy(() => withChunkRetry(() => import('views/pages/Tasks')));
const LogsPage = lazy(() => withChunkRetry(() => import('views/pages/Logs')));
const WebhooksView = lazy(() => withChunkRetry(() => import('views/pages/WebhooksView')));
const WebhookView = lazy(() => withChunkRetry(() => import('views/pages/WebhookView')));
const SystemStatusPage = lazy(() => withChunkRetry(() => import('views/pages/SystemStatus')));
const OrdersPage = lazy(() => withChunkRetry(() => import('views/pages/Orders')));
const OrderInformation = lazy(() => withChunkRetry(() => import('views/pages/OrderInformation')));
const OrderCreatePage = lazy(() => withChunkRetry(() => import('views/pages/OrderCreate')));
const CompanyPage = lazy(() => withChunkRetry(() => import('views/pages/Company')));
const AddMemberPage = lazy(() => withChunkRetry(() => import('views/pages/AddMember')));
const TeamPage = lazy(() => withChunkRetry(() => import('views/pages/Team')));
const UserDetailsPage = lazy(() => withChunkRetry(() => import('views/pages/UserDetailsPage')));
const NotFoundView = lazy(() => withChunkRetry(() => import('views/errors/NotFoundView')));
const EditMemberPage = lazy(() => withChunkRetry(() => import('views/pages/EditMember')));
const ProfileView = lazy(() => withChunkRetry(() => import('views/pages/ProfileView')));
const InvoicesView = lazy(() => withChunkRetry(() => import('views/pages/InvoicesView')));
const KeysPage = lazy(() => withChunkRetry(() => import('views/account/AccountView/Keys')));
const GenerateKeysPage = lazy(() => withChunkRetry(() => import('views/account/AccountView/GenerateKeys')));
const FieldsCustomizationPage = lazy(() => withChunkRetry(() => import('views/pages/FieldsCustomization')));
const CoveragePage = lazy(() => withChunkRetry(() => import('views/pages/Coverage')));
const SupportTicketsPage = lazy(() => withChunkRetry(() => import('views/pages/SupportTickets')));
const SupportTicketCreatePage = lazy(() => withChunkRetry(() => import('views/pages/TicketCreate')));
const SupportTicketCreatePageSelectCategory = lazy(() => withChunkRetry(() => import('views/pages/SelectSubcategory')));
const SupportTicketCreatePageFillDetails = lazy(() => withChunkRetry(() => import('views/pages/TicketFillDetails')));
const TicketInformationPage = lazy(() => withChunkRetry(() => import('views/pages/TicketInformation')));
const CustomSettingsPage = lazy(() => withChunkRetry(() => import('views/pages/CustomSettings')));
const BrandingPage = lazy(() => withChunkRetry(() => import('views/pages/BrandingCustomization')));
const TemplatesPage = lazy(() => withChunkRetry(() => import('views/pages/Templates')));
const TemplateCreatePage = lazy(() => withChunkRetry(() => import('views/pages/TemplateCreate')));
const TemplateEditPage = lazy(() => withChunkRetry(() => import('views/pages/TemplateEdit')));
const TwilioIntegrationPage = lazy(() => withChunkRetry(() => import('views/pages/TwilioIntegration')));
const MarketplacePage = lazy(() => withChunkRetry(() => import('views/pages/MarketplacePage')));
const ExternalIntegrationPage = lazy(() => withChunkRetry(() => import('views/pages/ExternalIntegrationPage')));
const PlatformsPage = lazy(() => withChunkRetry(() => import('views/pages/PlatformsPage')));
const VerificationPage = lazy(() => withChunkRetry(() => import('views/pages/VerificationPage')));
const VerificationStatusPage = lazy(() => withChunkRetry(() => import('views/pages/VerificationStatusPage')));

type GetRoutesParams = {
  defaultRedirectUrl: string;
  permissions?: Permissions;
  role?: string;
  isStaff: boolean;
};

export const getRoutes: (params: GetRoutesParams) => Routes = ({ defaultRedirectUrl, permissions, role, isStaff }) => {
  const isAdministrator = role === 'administrator';
  const canViewOrders = permissions?.has('can_view_orders');
  const canViewUsers = permissions?.has('can_view_profiles');
  const canViewCompany = permissions?.has('can_view_company_settings');

  return [
    {
      exact: true,
      path: '/404',
      component: NotFoundView,
    },
    {
      exact: true,
      guard: GuestGuard,
      path: '/login',
      component: LoginView,
    },
    {
      exact: true,
      guard: GuestGuard,
      path: '/signup',
      component: SignUpView,
    },
    {
      exact: true,
      path: urls.verificationStatus.url(),
      component: VerificationStatusPage,
    },
    {
      exact: true,
      path: urls.verification.url(),
      component: VerificationPage,
    },
    {
      path: '/app',
      guard: AuthGuard,
      routes: [
        {
          exact: true,
          path: '/app',
          layout: TwoPaneLayout,
          component: () => <Redirect to={defaultRedirectUrl} />,
        },

        {
          exact: true,
          path: urls.home.url(),
          layout: TwoPaneLayout,
          component: () =>
            defaultRedirectUrl === urls.home.url() ? <HomePage /> : <Redirect to={defaultRedirectUrl} />,
        },

        {
          exact: true,
          path: urls.emulator.url(),
          component: EmulatorPage,
          guard: PermissionGuard,
          permission: urls.emulator.permission,
          layout: FullScreenLayout,
        },

        /**
         * Usage menu
         */
        {
          exact: true,
          path: urls.usage.url(),
          layout: TwoPaneLayout,
          component: () => (canViewUsers ? <Redirect to={urls.users.url()} /> : <Redirect to={urls.orders.url()} />),
        },

        {
          exact: true,
          path: urls.users.url(),
          layout: TwoPaneLayout,
          component: UsersPage,
          guard: PermissionGuard,
          permission: urls.users.permission,
        },

        {
          exact: true,
          path: urls.user.url(),
          layout: TwoPaneLayout,
          component: UserDetailsPage,
          guard: PermissionGuard,
          permission: urls.user.permission,
        },

        {
          exact: true,
          path: urls.orders.url(),
          layout: TwoPaneLayout,
          component: OrdersPage,
          guard: PermissionGuard,
          permission: urls.orders.permission,
        },

        {
          exact: true,
          path: urls.orderInformation.url(),
          layout: TwoPaneLayout,
          component: OrderInformation,
          guard: PermissionGuard,
          permission: urls.orderInformation.permission,
        },

        {
          exact: true,
          path: urls.orderCreate.url(),
          layout: FullScreenLayout,
          component: OrderCreatePage,
          guard: PermissionGuard,
          permission: urls.orderCreate.permission,
        },

        {
          exact: true,
          path: urls.tasks.url(),
          layout: TwoPaneLayout,
          component: TasksPage,
          guard: PermissionGuard,
          permission: urls.tasks.permission,
        },

        {
          exact: true,
          path: urls.tasks.parameterUrl?.(),
          layout: TwoPaneLayout,
          component: TasksPage,
          guard: PermissionGuard,
          permission: urls.tasks.permission,
        },

        {
          exact: true,
          path: urls.coverage.url(),
          layout: TwoPaneLayout,
          component: CoveragePage,
          guard: PermissionGuard,
          permission: urls.coverage.permission,
        },

        /**
         * Development menu
         */
        {
          exact: true,
          path: urls.development.url(),
          layout: TwoPaneLayout,
          component: () => (canViewOrders ? <Redirect to={urls.keys.url()} /> : <Redirect to={urls.workflows.url()} />),
          guard: PermissionGuard,
          permission: urls.development.permission,
        },

        {
          exact: true,
          path: urls.workflows.url(),
          layout: TwoPaneLayout,
          component: WorkflowsPage,
          guard: PermissionGuard,
          permission: urls.workflows.permission,
        },

        {
          exact: true,
          path: urls.keys.url(),
          layout: TwoPaneLayout,
          component: KeysPage,
          guard: PermissionGuard,
          permission: urls.keys.permission,
        },

        {
          exact: true,
          path: urls.generateKeys.url(),
          layout: FullScreenLayout,
          component: GenerateKeysPage,
          guard: PermissionGuard,
          permission: urls.keys.permission,
        },

        {
          exact: true,
          path: urls.platformKeys.url(),
          layout: FullScreenLayout,
          component: ExternalIntegrationPage,
          guard: PermissionGuard,
          permission: urls.platforms.permission,
        },

        {
          exact: true,
          path: urls.platforms.url(),
          layout: TwoPaneLayout,
          component: PlatformsPage,
          guard: PermissionGuard,
          permission: urls.platforms.permission,
        },

        {
          exact: true,
          path: urls.logs.url(),
          layout: TwoPaneLayout,
          component: LogsPage,
          guard: PermissionGuard,
          permission: urls.logs.permission,
        },

        {
          exact: true,
          path: urls.webhooks.url(),
          layout: TwoPaneLayout,
          component: WebhooksView,
          guard: PermissionGuard,
          permission: urls.webhooks.permission,
        },

        {
          exact: true,
          path: urls.createWebhook.url(),
          layout: FullScreenLayout,
          component: WebhookView,
          guard: PermissionGuard,
          permission: urls.createWebhook.permission,
        },

        {
          exact: true,
          path: urls.editWebhook.url(),
          layout: FullScreenLayout,
          component: WebhookView,
          guard: PermissionGuard,
          permission: urls.editWebhook.permission,
        },

        {
          exact: true,
          path: urls.systemStatus.url(),
          layout: TwoPaneLayout,
          component: SystemStatusPage,
          guard: PermissionGuard,
          permission: urls.systemStatus.permission,
        },

        /**
         * Customization menu
         */
        {
          exact: true,
          path: urls.customization.url(),
          layout: TwoPaneLayout,
          component: () => <Redirect to={urls.branding.url()} />,
          guard: PermissionGuard,
          permission: urls.customization.permission,
        },

        {
          exact: true,
          path: urls.marketplace.url(),
          layout: TwoPaneLayout,
          component: () => (isStaff ? <MarketplacePage /> : <Redirect to={urls.branding.url()} />),
          guard: PermissionGuard,
          permission: urls.marketplace.permission,
        },

        {
          exact: true,
          path: urls.externalIntegration.url(),
          layout: FullScreenLayout,
          component: () => (isStaff ? <ExternalIntegrationPage /> : <Redirect to={urls.branding.url()} />),
          guard: PermissionGuard,
          permission: urls.externalIntegration.permission,
        },

        {
          exact: true,
          path: urls.account.url(),
          layout: TwoPaneLayout,
          component: () => <Redirect to={urls.branding.url()} />,
          guard: PermissionGuard,
          permission: urls.customization.permission,
        },

        {
          exact: true,
          path: urls.branding.url(),
          layout: TwoPaneLayout,
          component: BrandingPage,
          guard: PermissionGuard,
          permission: urls.customization.permission,
        },

        {
          exact: true,
          path: urls.customSettings.url(),
          layout: TwoPaneLayout,
          component: CustomSettingsPage,
          guard: PermissionGuard,
          permission: urls.customSettings.permission,
        },

        {
          exact: true,
          path: urls.twilioIntegration.url(),
          layout: FullScreenLayout,
          component: TwilioIntegrationPage,
          guard: PermissionGuard,
          permission: urls.twilioIntegration.permission,
        },

        {
          exact: true,
          path: urls.templateCreate.url(),
          layout: FullScreenLayout,
          component: TemplateCreatePage,
          guard: PermissionGuard,
          permission: urls.templateCreate.permission,
        },

        {
          exact: true,
          path: urls.templateEdit.url(),
          layout: FullScreenLayout,
          component: TemplateEditPage,
          guard: PermissionGuard,
          permission: urls.templateEdit.permission,
        },

        {
          exact: true,
          path: urls.templates.url(),
          layout: TwoPaneLayout,
          component: TemplatesPage,
          guard: PermissionGuard,
          permission: urls.templates.permission,
        },

        {
          exact: true,
          path: urls.fieldsCustomization.url(),
          layout: TwoPaneLayout,
          component: FieldsCustomizationPage,
          guard: PermissionGuard,
          permission: urls.fieldsCustomization.permission,
        },

        /**
         * Help menu
         */
        {
          exact: true,
          path: urls.supportTickets.url(),
          layout: TwoPaneLayout,
          guard: PermissionGuard,
          permission: urls.supportTickets.permission,
          component: SupportTicketsPage,
        },
        {
          exact: true,
          path: urls.createSupportTicket.url(),
          guard: PermissionGuard,
          permission: urls.supportTickets.permission,
          component: SupportTicketCreatePage,
        },
        {
          exact: true,
          path: urls.supportSelectCategory.url(),
          guard: PermissionGuard,
          permission: urls.supportTickets.permission,
          component: SupportTicketCreatePageSelectCategory,
        },
        {
          exact: true,
          path: urls.supportFillDetails.url(),
          guard: PermissionGuard,
          permission: urls.supportTickets.permission,
          component: SupportTicketCreatePageFillDetails,
        },
        {
          exact: true,
          path: urls.ticketInformation.url(),
          layout: TwoPaneLayout,
          guard: PermissionGuard,
          permission: urls.ticketInformation.permission,
          component: TicketInformationPage,
        },

        /**
         * Settings menu
         */
        {
          exact: true,
          path: urls.settings.url(),
          layout: TwoPaneLayout,
          component: () => {
            if (isAdministrator && canViewOrders) {
              return <Redirect to={urls.team.parameterUrl?.() ?? '/'} />;
            }

            if (canViewCompany) {
              return <Redirect to={urls.company.url()} />;
            }

            return <Redirect to={urls.profile.url()} />;
          },
          guard: PermissionGuard,
          permission: urls.settings.permission,
        },

        {
          exact: true,
          path: urls.company.url(),
          layout: TwoPaneLayout,
          component: CompanyPage,
          guard: PermissionGuard,
          permission: urls.company.permission,
        },

        {
          exact: true,
          path: urls.addMember.url(),
          layout: FullScreenLayout,
          component: AddMemberPage,
          guard: PermissionGuard,
          permission: urls.addMember.permission,
        },

        {
          exact: true,
          path: urls.editMember.url(),
          layout: FullScreenLayout,
          component: EditMemberPage,
          guard: PermissionGuard,
          permission: urls.editMember.permission,
        },

        {
          exact: true,
          path: urls.team.url(),
          layout: TwoPaneLayout,
          component: TeamPage,
          guard: PermissionGuard,
          permission: urls.team.permission,
        },

        {
          exact: true,
          path: urls.invoices.url(),
          layout: TwoPaneLayout,
          component: InvoicesView,
          guard: PermissionGuard,
          permission: urls.billing.permission,
        },

        {
          exact: true,
          path: urls.profile.url(),
          layout: TwoPaneLayout,
          component: ProfileView,
          guard: PermissionGuard,
          permission: urls.profile.permission,
        },

        /**
         * Redirects for old URLs
         */
        ...Object.values(urls)
          .filter((item) => Boolean(item.oldUrl))
          .map<Routes[number]>((item) => ({
            exact: true,
            path: item.oldUrl,
            component: () => <Redirect to={item.url()} />,
          })),

        {
          component: () => <Redirect to="/404" />,
        },
      ],
    },
    {
      path: '*',
      routes: [
        {
          exact: true,
          guard: AuthGuard,
          path: '/',
          component: () => <Redirect to={defaultRedirectUrl} />,
        },
        {
          component: () => <Redirect to="/404" />,
        },
      ],
    },
  ];
};

export function RoutesWrapper() {
  const { user } = useAuth();

  const profileSettings = user?.profile;
  const role = profileSettings?.role.id;
  const isStaff = !!profileSettings?.staff_dashboard_url;

  const { permissions } = useAuth();
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const defaultRedirectUrl = (() => {
    switch (true) {
      case role === 'orders_manager':
        return urls.orders.url();
      case user?.is_target_account && user?.properties && !user.properties.targetVisited:
        return urls.coverage.url();
      case params.has('backUrl'):
        return params.get('backUrl') || urls.home.url();
      default:
        return urls.home.url();
    }
  })();

  const routes = getRoutes({ defaultRedirectUrl, permissions, role, isStaff });

  return <RenderRoutes defaultRedirectUrl={defaultRedirectUrl} routes={routes} />;
}
